/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  CarrierParcelTemplate,
  CarrierParcelTemplate$inboundSchema,
  CarrierParcelTemplate$Outbound,
  CarrierParcelTemplate$outboundSchema,
} from "./carrierparceltemplate.js";
import {
  DistanceUnitEnum,
  DistanceUnitEnum$inboundSchema,
  DistanceUnitEnum$outboundSchema,
} from "./distanceunitenum.js";
import {
  WeightUnitEnum,
  WeightUnitEnum$inboundSchema,
  WeightUnitEnum$outboundSchema,
} from "./weightunitenum.js";

export type UserParcelTemplate = {
  /**
   * The measure unit used for length, width and height.
   */
  distanceUnit?: DistanceUnitEnum | undefined;
  /**
   * The height of the package, in units specified by the `distance_unit` attribute. Required, but if using a preset carrier template then this field must be empty.
   */
  height?: string | undefined;
  /**
   * The length of the package, in units specified by the `distance_unit` attribute. Required, but if using a preset carrier template then this field must be empty.
   */
  length?: string | undefined;
  /**
   * The name of the User Parcel Template
   */
  name?: string | undefined;
  /**
   * The weight of the package, in units specified by the weight_unit attribute.
   */
  weight?: string | undefined;
  /**
   * The unit used for weight.
   */
  weightUnit?: WeightUnitEnum | undefined;
  /**
   * The width of the package, in units specified by the `distance_unit` attribute. Required, but if using a preset carrier template then this field must be empty.
   */
  width?: string | undefined;
  /**
   * Date and time of User Parcel Template creation
   */
  objectCreated?: Date | undefined;
  /**
   * Unique identifier of the given User Parcel Template object
   */
  objectId?: string | undefined;
  /**
   * Username of the user who created the User Parcel Template object
   */
  objectOwner?: string | undefined;
  /**
   * Date and time of last update on User Parcel Template
   */
  objectUpdated?: Date | undefined;
  template?: CarrierParcelTemplate | undefined;
};

/** @internal */
export const UserParcelTemplate$inboundSchema: z.ZodType<
  UserParcelTemplate,
  z.ZodTypeDef,
  unknown
> = z.object({
  distance_unit: DistanceUnitEnum$inboundSchema.optional(),
  height: z.string().optional(),
  length: z.string().optional(),
  name: z.string().optional(),
  weight: z.string().optional(),
  weight_unit: WeightUnitEnum$inboundSchema.optional(),
  width: z.string().optional(),
  object_created: z.string().datetime({ offset: true }).transform(v =>
    new Date(v)
  ).optional(),
  object_id: z.string().optional(),
  object_owner: z.string().optional(),
  object_updated: z.string().datetime({ offset: true }).transform(v =>
    new Date(v)
  ).optional(),
  template: CarrierParcelTemplate$inboundSchema.optional(),
}).transform((v) => {
  return remap$(v, {
    "distance_unit": "distanceUnit",
    "weight_unit": "weightUnit",
    "object_created": "objectCreated",
    "object_id": "objectId",
    "object_owner": "objectOwner",
    "object_updated": "objectUpdated",
  });
});

/** @internal */
export type UserParcelTemplate$Outbound = {
  distance_unit?: string | undefined;
  height?: string | undefined;
  length?: string | undefined;
  name?: string | undefined;
  weight?: string | undefined;
  weight_unit?: string | undefined;
  width?: string | undefined;
  object_created?: string | undefined;
  object_id?: string | undefined;
  object_owner?: string | undefined;
  object_updated?: string | undefined;
  template?: CarrierParcelTemplate$Outbound | undefined;
};

/** @internal */
export const UserParcelTemplate$outboundSchema: z.ZodType<
  UserParcelTemplate$Outbound,
  z.ZodTypeDef,
  UserParcelTemplate
> = z.object({
  distanceUnit: DistanceUnitEnum$outboundSchema.optional(),
  height: z.string().optional(),
  length: z.string().optional(),
  name: z.string().optional(),
  weight: z.string().optional(),
  weightUnit: WeightUnitEnum$outboundSchema.optional(),
  width: z.string().optional(),
  objectCreated: z.date().transform(v => v.toISOString()).optional(),
  objectId: z.string().optional(),
  objectOwner: z.string().optional(),
  objectUpdated: z.date().transform(v => v.toISOString()).optional(),
  template: CarrierParcelTemplate$outboundSchema.optional(),
}).transform((v) => {
  return remap$(v, {
    distanceUnit: "distance_unit",
    weightUnit: "weight_unit",
    objectCreated: "object_created",
    objectId: "object_id",
    objectOwner: "object_owner",
    objectUpdated: "object_updated",
  });
});

/**
 * @internal
 * @deprecated This namespace will be removed in future versions. Use schemas and types that are exported directly from this module.
 */
export namespace UserParcelTemplate$ {
  /** @deprecated use `UserParcelTemplate$inboundSchema` instead. */
  export const inboundSchema = UserParcelTemplate$inboundSchema;
  /** @deprecated use `UserParcelTemplate$outboundSchema` instead. */
  export const outboundSchema = UserParcelTemplate$outboundSchema;
  /** @deprecated use `UserParcelTemplate$Outbound` instead. */
  export type Outbound = UserParcelTemplate$Outbound;
}

export function userParcelTemplateToJSON(
  userParcelTemplate: UserParcelTemplate,
): string {
  return JSON.stringify(
    UserParcelTemplate$outboundSchema.parse(userParcelTemplate),
  );
}

export function userParcelTemplateFromJSON(
  jsonString: string,
): SafeParseResult<UserParcelTemplate, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => UserParcelTemplate$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'UserParcelTemplate' from JSON`,
  );
}
